import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import ErrorMessage from './ErrorMessage';

export const TextInput = (props) => {
    const { name, label, ariaLabel, value, minLength, maxLength, required, setFieldValue, fieldErrors, disabled } = props;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFieldValue(prevState => ({ ...prevState, [name]: value }));
    }

    return <>
        <input type="text"
            id={name}
            name={name}
            data-label={label}
            aria-label={ariaLabel}
            value={value || ''}
            minLength={minLength}
            maxLength={maxLength || 255}
            aria-invalid={!!fieldErrors[name]}
            onChange={handleChange}
            aria-required={required}
            required={required}
            disabled={disabled}
        />
        <ErrorMessage message={fieldErrors[name]} />
    </>;
}
TextInput.defaultProps = {
    //don't pass in fieldErrors, or label if input does not need validation (required, invalid format, etc.)
    fieldErrors: { name: undefined },
    label: undefined
}

export const NumberInput = (props) => {
    const { name, label, ariaLabel, value, min, max, required, setFieldValue, fieldErrors } = props;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFieldValue(prevState => ({ ...prevState, [name]: value }));
    }

    return <>
        <input type="number"
            id={name}
            name={name}
            data-label={label}
            aria-label={ariaLabel}
            value={value || ''}
            min={min}
            max={max}
            onChange={handleChange}
            aria-invalid={!!fieldErrors[name]}
            aria-required={required}
            required={required}
        />
        <ErrorMessage message={fieldErrors[name]} />
    </>
}

NumberInput.defaultProps = {
    //don't pass in fieldErrors, or label if input does not need validation (required, invalid format, etc.)
    fieldErrors: { name: undefined },
    label: undefined
}

export const DateTimeInput = (props) => { //Do not use if time portion of value ever needs to be null
    const { name, label, ariaLabel, value, required, setFieldValue, fieldErrors, min, max } = props;
    //DateTimeInputs always need validation to look out for invalid format

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFieldValue(prevState => ({ ...prevState, [name]: value }));
    }

    return <>
        <input type="datetime-local"
            id={name}
            name={name}
            data-label={label}
            aria-label={ariaLabel}
            value={value ? moment(value).format('YYYY-MM-DDTHH:mm') : ''}
            min={moment(min || '1900-01-01').format('YYYY-MM-DDTHH:mm')}
            max={moment(max || '9999-12-31').format('YYYY-MM-DDTHH:mm')}
            aria-invalid={!!fieldErrors[name]}
            aria-required={required}
            required={required}
            onChange={handleChange}
        />
        <ErrorMessage message={fieldErrors[name]} />
    </>;
}

export const DateInput = (props) => {
    const { name, label, ariaLabel, value, required, setFieldValue, fieldErrors, min, max } = props;
    //DateInputs always need validation to look out for invalid format

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFieldValue(prevState => ({ ...prevState, [name]: value }));
    }

    return <>
        <input type="date"
            id={name}
            name={name}
            data-label={label}
            aria-label={ariaLabel}
            value={value ? moment(value).format('YYYY-MM-DD') : ''}
            min={moment(min || '1900-01-01').format('YYYY-MM-DD')}
            max={moment(max || '9999-12-31').format('YYYY-MM-DD')}
            aria-invalid={!!fieldErrors[name]}
            aria-required={required}
            required={required}
            onChange={handleChange}
        />
        <ErrorMessage message={fieldErrors[name]} />
    </>;
}

export const TimeInput = (props) => {
    const { name, label, ariaLabel, value, required, setFieldValue, fieldErrors, disabled } = props;
    //TimeInputs always need validation to look out for invalid format

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFieldValue(prevState => ({ ...prevState, [name]: value }));
    }

    return <>
        <input type="time"
            id={name}
            name={name}
            data-label={label}
            aria-label={ariaLabel}
            value={value ? moment(value, 'HH:mm').format('HH:mm') : ''}
            aria-invalid={!!fieldErrors[name]}
            aria-required={required}
            required={required}
            disabled={disabled}
            onChange={handleChange}
        />
        <ErrorMessage message={fieldErrors[name]} />
    </>;
}

export const SelectInput = (props) => {
    const { name, label, ariaLabel, value, required, setFieldValue, fieldErrors, disabled, children } = props;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFieldValue(prevState => ({ ...prevState, [name]: value }));
    }

    return <>
        <div className="select">
            {/*<span className="icon-down-arrow" aria-hidden="true"></span>*/}
            <select
                id={name}
                name={name}
                value={value || ''}
                data-label={label}
                aria-label={ariaLabel}
                aria-invalid={!!fieldErrors[name]}
                aria-required={required}
                required={required}
                disabled={disabled}
                onChange={handleChange}
            >
                {/* <option value="">Select</option> */}
                {children}
            </select>
        </div>
        <ErrorMessage message={fieldErrors[name]} />
    </>;
}
SelectInput.defaultProps = {
    //don't pass in fieldErrors, or label if input does not need validation (required, invalid format, etc.)
    fieldErrors: { name: undefined },
    label: undefined
}

export const RadioInput = (props) => {
    const { id, name, value, label, ariaLabel, checked, setFieldValue, required } = props;

    const handleChange = (e) => {
        const { name, value } = e.target;
        //force value to be int or bool, not string.
        const val = Number.isInteger(Number(value)) ? Number(value) : value === 'true';
        setFieldValue(prevState => ({ ...prevState, [name]: val }));
    }

    //required/invalid attributes (i.e. aria-invalid, required, and aria-required) also go on the <fieldset>
    return <>
        <input
            type="radio"
            id={id}
            name={name}
            value={value}
            data-label={label}
            aria-label={ariaLabel}
            checked={checked}
            onChange={handleChange}
            required={required} 
        />
    </>;
}

RadioInput.defaultProps = {
    //don't pass in fieldErrors, or label if input does not need validation (required, invalid format, etc.)
    fieldErrors: { name: undefined },
    label: undefined
}

export const CheckboxInput = (props) => {
    const { name, label, ariaLabel, checked, required, setFieldValue, fieldErrors } = props;

    const handleChange = (e) => {
        const { name, checked } = e.target;
        setFieldValue(prevState => ({ ...prevState, [name]: checked }));
    }
    
    return <>
        <input
            type="checkbox"
            id={name}
            name={name}
            checked={checked}
            data-label={label}
            aria-label={ariaLabel}
            aria-required={required}
            aria-invalid={!!fieldErrors[name]}
            required={required}
            onChange={handleChange}
        />
        <ErrorMessage message={fieldErrors[name]} />
    </>;
}

CheckboxInput.defaultProps = {
    //don't pass in fieldErrors, or label if input does not need validation (required, invalid format, etc.)
    fieldErrors: { name: undefined },
    label: undefined
}

export const TextAreaInput = (props) => {
    const { name, label, ariaLabel, value, maxLength, required, setFieldValue, fieldErrors, setFieldErrors, disabled } = props;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFieldValue(prevState => ({ ...prevState, [name]: value }));
    }

    useEffect(() => {
        //Add this to any component that needs to clear value after disabling
        if (disabled) { //When field is disabled, clear the field value
            setFieldValue(prevState => ({ ...prevState, [name]: null }));
        };
    }, [disabled]);

    useEffect(() => {
        //Add this to any component that needs to clear value after hiding and unmounting
        return () => { //When component hides/unmounts, clear the field value and validation
            setFieldValue(prevState => ({ ...prevState, [name]: null }));
            setFieldErrors(prevState => ({ ...prevState, [name]: null }));
        };
    }, []);

    return <>
        <textarea
            id={name}
            name={name}
            data-label={label}
            aria-label={ariaLabel}
            required={required}
            aria-required={required}
            aria-invalid={!!fieldErrors[name]}
            value={value || ''}
            onChange={handleChange}
            maxLength={maxLength || 2500}
            disabled={disabled}
        >
        </textarea>
        <ErrorMessage message={fieldErrors[name]} />
    </>
}

TextAreaInput.defaultProps = {
    //don't pass in fieldErrors, setFieldErrors, or label if input does not need validation (required, invalid format, etc.)
    fieldErrors: { name: undefined },
    setFieldErrors: () => { },
    label: undefined
}

//<AutoCompleteInput name="mdService" label="MD Service" value={caseInfo.mdService} getOptions={getMDServices} setFieldValue={setCaseInfo} fieldErrors={fieldErrors} preventCustomInput={true} />
export const AutoCompleteInput = (props) => { //Filter options on API
    const { name, value, label, maxLength, setFieldValue, fieldErrors, getOptions, preventCustomInput } = props;
    const [visibleOptions, setVisibleOptions] = useState([]);
    const [inputMatchesOption, setInputMatchesOption] = useState(true); //Any initial value is considered a match
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const autoCompleteRef = useRef(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFieldValue(prevState => ({ ...prevState, [name]: value }));
        setFocusedIndex(-1);
        getOptions(value).then((options) => { //options needs to be in the form of [{description: 'option 1'}, {description: 'option 2'}]
            setVisibleOptions(options);
            const matchingOption = options.find(o => o.description.toLowerCase() === value.toLowerCase());
            setInputMatchesOption(!!matchingOption);
        });
    }

    const handleOptionSelect = (e, selectedOption) => {
        e.preventDefault();
        setFieldValue(prevState => ({ ...prevState, [name]: selectedOption }));
        setVisibleOptions([]); //hide options if user selects an option
        setInputMatchesOption(true);
        setFocusedIndex(-1);
    }

    const handleBlur = (e) => {
        //Clear options when user focuses outside of the AutoCompleteInput
        const autoCompleteHasFocus = autoCompleteRef.current.contains(e.relatedTarget);
        if (!autoCompleteHasFocus) {
            setVisibleOptions([]);
        }
    }

    const handleKeyDown = (e) => {
        switch (e.key) {
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex(prevIndex => Math.max(prevIndex - 1, 0));
                break;
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex(prevIndex => Math.min(prevIndex + 1, visibleOptions.length - 1));
                break;
            case 'Enter':
                if (focusedIndex !== -1) {
                    e.preventDefault();
                    handleOptionSelect(e, visibleOptions[focusedIndex].description);
                }
                break;
            default:
                break;
        }
    }

    useEffect(() => {
        //Get options on load to see if default value is a match
        getOptions(value).then((options) => { //options needs to be in the form of [{description: 'option 1'}, {description: 'option 2'}]
            setVisibleOptions([]);
            const matchingOption = options.find(o => o.description.toLowerCase() === value.toLowerCase());
            setInputMatchesOption(!!matchingOption);
        });
    }, []);

    return <>
        <div className="autocomplete-list" ref={autoCompleteRef}>
            <input
                type="text"
                id={name}
                name={name}
                data-label={label}
                value={value || ''}
                maxLength={maxLength || 255}
                aria-invalid={!!fieldErrors[name]}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                pattern={preventCustomInput && !inputMatchesOption ? '(?!x)x' : undefined} //Throw 'Invalid' validation if custom input is not allowed
                data-customvalidationmessage={preventCustomInput && !inputMatchesOption ? `${label} Must Be Selected from the List Provided` : undefined}
                aria-expanded={visibleOptions.length > 0}
                aria-autocomplete="list"
                aria-controls={`${name}-list`}
                role="combobox"
            />
            <ul id={`${name}-list`}>
                {visibleOptions.map((option, i) => (
                    <li key={i}>
                        <a className={`item-list-link ${focusedIndex === i ? 'focused' : ''}`}
                            onClick={(e) => { handleOptionSelect(e, option.description) }}
                            aria-selected={focusedIndex === i}
                            tabIndex="-1"
                            role="option"
                            href="/#">
                            {option.description}
                        </a>
                    </li>
                ))}
            </ul>
        </div>
        <ErrorMessage message={fieldErrors[name]} />
    </>;
}

AutoCompleteInput.defaultProps = {
    //don't pass in fieldErrors, or label if input does not need validation (required, invalid format, etc.)
    fieldErrors: { name: undefined },
    label: undefined
}